import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Calendar, MapPin, ChevronRight, Users as UsersIcon, LogOut, ShieldCheck } from "lucide-react";
import GlassPanel from "@/components/GlassPanel";
import VolunteerEventDeck from "@/components/VolunteerEventDeck";
import { useAuth, formatApiError } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Volunteer landing page. Volunteers see the events they've been assigned
 * to score. Selecting one opens the per-test Scoring Deck. Athletes without
 * the volunteer flag are redirected back to /my-gps.
 */
export default function VolunteerScoring() {
  const { user, loading: authLoading, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // event obj

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/volunteer/me/events");
      setEvents(data.events || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.is_volunteer || user?.role === "admin") load();
  }, [user, load]);

  if (authLoading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <div className="gps-mono text-cyan text-sm tracking-[0.3em]">CONNECTING...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_volunteer && user.role !== "admin") return <Navigate to="/my-gps" replace />;

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="volunteer-page">
      {selected ? (
        <VolunteerEventDeck event={selected} onBack={() => setSelected(null)} />
      ) : (
        <>
          <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
            <div>
              <div className="gps-tag mb-2">VOLUNTEER SCORING</div>
              <h1 className="gps-heading text-4xl sm:text-5xl">SCORING DECK</h1>
              <div className="text-mute gps-mono text-[11px] tracking-[0.2em] mt-2">
                {user.name?.toUpperCase()} · VOLUNTEER · {events.length} EVENT{events.length === 1 ? "" : "S"} ASSIGNED
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="gps-btn ghost"
              data-testid="volunteer-logout"
              aria-label="Log out"
            >
              <LogOut size={14} /> LOGOUT
            </button>
          </div>

          {loading ? (
            <GlassPanel className="p-12 text-center" scanline>
              <div className="gps-mono text-cyan text-sm tracking-[0.3em]">LOADING EVENTS…</div>
            </GlassPanel>
          ) : events.length === 0 ? (
            <GlassPanel className="p-12 text-center" scanline data-testid="volunteer-empty">
              <div className="gps-mono text-mute text-xs tracking-[0.3em]">
                NO EVENTS ASSIGNED · ASK AN ADMIN TO ADD YOU TO AN EVENT
              </div>
            </GlassPanel>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="volunteer-event-grid">
              {events.map((ev) => {
                const dateStr = ev.date ? new Date(ev.date).toLocaleDateString(undefined, {
                  year: "numeric", month: "short", day: "2-digit",
                }) : "TBD";
                const statusColor = ev.status === "live" ? "text-emerald-400"
                  : ev.status === "upcoming" ? "text-cyan-400"
                  : "text-mute";
                return (
                  <button
                    key={ev.id}
                    onClick={() => setSelected(ev)}
                    className="group text-left"
                    data-testid={`volunteer-event-${ev.id}`}
                  >
                    <GlassPanel className="p-5 hover:border-cyan-400/60 transition-colors" scanline>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className={`gps-mono text-[10px] tracking-[0.25em] ${statusColor}`}>
                            {(ev.status || "").toUpperCase()}
                          </div>
                          <div className="gps-heading text-xl text-white mt-1 truncate">{ev.name}</div>
                          <div className="text-mute gps-mono text-[11px] tracking-[0.15em] mt-3 flex items-center gap-2">
                            <Calendar size={12} /> {dateStr}
                          </div>
                          {ev.location && (
                            <div className="text-mute gps-mono text-[11px] tracking-[0.15em] mt-1 flex items-center gap-2">
                              <MapPin size={12} /> <span className="truncate">{ev.location}</span>
                            </div>
                          )}
                          <div className="text-mute gps-mono text-[11px] tracking-[0.15em] mt-1 flex items-center gap-2">
                            <UsersIcon size={12} /> {ev.interested_count || 0} INTERESTED
                          </div>
                        </div>
                        <ChevronRight
                          size={18}
                          className="text-mute group-hover:text-cyan-400 transition-colors mt-1"
                        />
                      </div>
                      <div className="mt-4 flex items-center gap-2 gps-mono text-[10px] tracking-[0.2em] text-cyan-400">
                        <ShieldCheck size={12} /> OPEN SCORING DECK
                      </div>
                    </GlassPanel>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
